import { createSelector } from '@reduxjs/toolkit';
import {
  categoriesAdapter,
  initialResourcesState,
  modelsAdapter,
} from 'services/api/resources/adapters';
import { resourcesApi } from 'services/api/resources/queries';
import { ResourceFilters } from 'services/api/resources/types';
import { RootState } from 'services/store';
import { Category } from 'types/entities/Resources';

/**
 * Access the singleton cache.
 * Since the endpoint uses `serializeQueryArgs` to enforce a single cache key,
 * we use a static argument ('shared') to retrieve the active data regardless of the current locale.
 */
const selectResourcesResult = resourcesApi.endpoints.getResources.select({
  locale: 'shared',
});

const selectResourcesData = createSelector(
  selectResourcesResult,
  result => result.data ?? initialResourcesState,
);

export const {
  selectAll: selectAllCategories,
  selectEntities: selectCategoryEntities,
  selectTotal,
} = categoriesAdapter.getSelectors(
  (state: RootState) => selectResourcesData(state).categories,
);

export const {
  selectAll: selectAllModels,
  selectEntities: selectModelEntities,
  selectById: selectModelById,
} = modelsAdapter.getSelectors(
  (state: RootState) => selectResourcesData(state).models,
);

export const selectBaseFilteredModels = createSelector(
  [selectAllModels, (_state: RootState, filters: ResourceFilters) => filters],
  (allModels, filters) => {
    const { compatibleIds, excludeDiscontinued } = filters;

    const compatibleIdsSet = compatibleIds ? new Set(compatibleIds) : null;

    if (!compatibleIdsSet && !excludeDiscontinued) {
      return allModels;
    }

    return allModels.filter(model => {
      if (excludeDiscontinued && model.discontinuedDate) {
        return false;
      }

      if (compatibleIdsSet && !compatibleIdsSet.has(model.model)) {
        return false;
      }

      return true;
    });
  },
);

const selectCategoryChildrenMap = createSelector(
  [selectAllCategories],
  categories => {
    const map: Record<number, number[]> = {};
    categories.forEach(cat => {
      if (cat.parentId) {
        if (!map[cat.parentId]) map[cat.parentId] = [];
        map[cat.parentId].push(cat.id);
      }
    });
    return map;
  },
);

export const selectRootCategories = createSelector(
  [selectAllCategories],
  categories => categories.filter(c => !c.parentId),
);

export const selectSubCategoriesByParentId = createSelector(
  [selectAllCategories, (state, parentId: number) => parentId],
  (categories, parentId) => categories.filter(c => c.parentId === parentId),
);

export const selectCategoryBySlugPath = createSelector(
  [selectAllCategories, (state, slugs: string[]) => slugs],
  (allCategories, slugs) => {
    if (!slugs || slugs.length === 0) return null;

    let currentParentId: number | undefined = undefined;
    let foundCategory: Category | undefined;

    for (const slug of slugs) {
      const parentIdToCheck = currentParentId;

      foundCategory = allCategories.find(
        cat => cat.slug === slug && cat.parentId === parentIdToCheck,
      );

      if (!foundCategory) {
        return null;
      }

      currentParentId = foundCategory.id;
    }

    return foundCategory;
  },
);

export const selectCategorySlugPathById = createSelector(
  [
    selectCategoryEntities,
    (state: RootState, categoryId: number | undefined) => categoryId,
  ],
  (entities, categoryId) => {
    if (!categoryId) return null;

    const slugs: string[] = [];
    let current = entities[categoryId];

    while (current) {
      if (current.slug) {
        slugs.unshift(current.slug);
      }

      if (!current.parentId) break;

      current = entities[current.parentId];
    }

    return slugs.join('/');
  },
);

export const selectBreadcrumbsFromPath = createSelector(
  [selectAllCategories, (state: RootState, pathString: string) => pathString],
  (allCategories, pathString) => {
    if (!pathString) return [];

    const slugs = pathString.split('/').filter(Boolean);
    const breadcrumbs: Category[] = [];

    let currentParentId: number | undefined = undefined;

    for (const slug of slugs) {
      const parentIdToCheck = currentParentId;

      const category = allCategories.find(
        cat => cat.slug === slug && cat.parentId === parentIdToCheck,
      );

      if (category) {
        breadcrumbs.push(category);
        currentParentId = category.id;
      } else {
        break;
      }
    }

    return breadcrumbs;
  },
);

export const selectCanonicalSlugPathByCategoryId = createSelector(
  [selectCategoryEntities, (state, categoryId: number) => categoryId],
  (entities, categoryId) => {
    const slugs: string[] = [];
    let current = entities[categoryId];

    while (current) {
      if (current.slug) {
        slugs.unshift(current.slug);
      }
      if (!current.parentId) break;
      current = entities[current.parentId];
    }

    return slugs.join('/');
  },
);

export const selectCategoryCounts = createSelector(
  [selectAllCategories, selectBaseFilteredModels, selectCategoryChildrenMap],
  (allCategories, filteredModels, categoryChildrenMap) => {
    const directCounts: Record<number, number> = {};

    filteredModels.forEach(m => {
      directCounts[m.categoryId] = (directCounts[m.categoryId] || 0) + 1;
    });

    const totalCounts: Record<number, number> = {};

    const getCountForCategory = (catId: number): number => {
      if (totalCounts[catId] !== undefined) return totalCounts[catId];

      let count = directCounts[catId] || 0;

      const children = categoryChildrenMap[catId] || [];
      children.forEach(childId => {
        count += getCountForCategory(childId);
      });

      totalCounts[catId] = count;
      return count;
    };

    allCategories.forEach(cat => getCountForCategory(cat.id));

    return totalCounts;
  },
);

export const selectCompatibleIdsByMachineId = createSelector(
  [selectAllModels, (state, machineId: string | null) => machineId],
  (allModels, machineId) => {
    if (!machineId) return null;

    const compatibleIds = new Set<string>();
    const currentMachine = allModels.find(m => m.model === machineId);

    if (currentMachine?.compatibleAttachments) {
      currentMachine.compatibleAttachments.forEach(id => compatibleIds.add(id));
    }

    allModels.forEach(otherModel => {
      if (otherModel.compatibleAttachments?.includes(machineId)) {
        compatibleIds.add(otherModel.model);
      }
    });

    return Array.from(compatibleIds);
  },
);

export const selectVisibleCategoryIds = createSelector(
  [selectAllCategories, selectBaseFilteredModels, selectCategoryChildrenMap],
  (allCategories, filteredModels, categoryChildrenMap) => {
    if (filteredModels.length === 0) return new Set<number>();

    const visibleCategoryIds = new Set<number>();

    filteredModels.forEach(m => {
      visibleCategoryIds.add(m.categoryId);
    });

    const checkCategoryVisibility = (catId: number): boolean => {
      if (visibleCategoryIds.has(catId)) return true;

      const children = categoryChildrenMap[catId] || [];

      let hasVisibleChildren = false;
      for (const childId of children) {
        if (checkCategoryVisibility(childId)) {
          hasVisibleChildren = true;
        }
      }

      if (hasVisibleChildren) {
        visibleCategoryIds.add(catId);
        return true;
      }

      return false;
    };

    allCategories.forEach(cat => checkCategoryVisibility(cat.id));

    return visibleCategoryIds;
  },
);

export const selectFilteredModels = createSelector(
  [
    (state: RootState, categoryId: number, filters: ResourceFilters) =>
      selectBaseFilteredModels(state, filters),
    (state: RootState, categoryId: number) => categoryId,
  ],
  (filteredModels, categoryId) => {
    return filteredModels.filter(m => m.categoryId === categoryId);
  },
);

export const selectModelsBySearchQueryAndCategory = createSelector(
  [
    (
      state: RootState,
      query: string,
      categoryId: number | undefined,
      filters: ResourceFilters,
    ) => selectBaseFilteredModels(state, filters),
    selectCategoryChildrenMap,
    (state, searchQuery: string) => searchQuery,
    (state, searchQuery: string, categoryId?: number) => categoryId,
  ],
  (filteredModels, childrenMap, query, categoryId) => {
    let scopedModels = filteredModels;

    if (categoryId) {
      const allowedCategoryIds = new Set<number>();
      const queue = [categoryId];

      while (queue.length > 0) {
        const currentId = queue.shift()!;
        allowedCategoryIds.add(currentId);
        const children = childrenMap[currentId];
        if (children) {
          children.forEach(childId => queue.push(childId));
        }
      }
      scopedModels = filteredModels.filter(m =>
        allowedCategoryIds.has(m.categoryId),
      );
    }

    if (!query || query.trim().length < 1) {
      return scopedModels;
    }

    const lowerQuery = query.toLowerCase().trim();

    return scopedModels.filter(
      ({ model, modelDescription }) =>
        model.toLowerCase().includes(lowerQuery) ||
        modelDescription?.toLowerCase().includes(lowerQuery),
    );
  },
);
